import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

export default function AgenticQAPage() {
  const qaLifecycleSteps = [
    {
      title: 'Requirement Analysis',
      points: [
        'Understand requirements and user intent',
        'Extract features, rules, and constraints',
        'Identify stakeholders and workflows'
      ]
    },
    {
      title: 'Test Design',
      points: [
        'Define test objectives and test types',
        'Prioritize by risk and business impact',
        'Map requirement traceability'
      ]
    },
    {
      title: 'Test Scenario Creation',
      points: [
        'Generate positive and negative scenarios',
        'Cover edge cases and alternate flows',
        'Identify data and boundary conditions'
      ]
    },
    {
      title: 'Test Case Development',
      points: [
        'Convert scenarios into detailed cases',
        'Define expected results and conditions',
        'Create automation-ready skeletons'
      ]
    },
    {
      title: 'Test Execution',
      points: [
        'Run manual and automated tests',
        'Compare actual versus expected results',
        'Detect failures and anomalies'
      ]
    },
    {
      title: 'Bug Raising',
      points: [
        'Analyze failures and group duplicates',
        'Auto-generate bug details and evidence',
        'Suggest probable root cause'
      ]
    }
  ];

  return (
    <div className="agenticqa-page" style={{ background: '#f8f9fa', minHeight: '100vh', paddingBottom: '3rem' }}>
      <SEO pageName="Agentic QA" keywords={["Agentic QA", "AI Productivity", "QA Engineering", "Context Engineering"]} />
      {/* Hero Banner */}
      <section style={{ padding: '4rem 1.5rem 2.5rem 1.5rem', background: 'linear-gradient(135deg, #e3e8ee 0%, #fff 100%)', textAlign: 'center', position: 'relative' }}>
        <img src="/images/icons/ai-qa-abstract.png" alt="Agentic QA Abstract" style={{ width: 120, marginBottom: 24, opacity: 0.9 }} />
        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#2c3e50', marginBottom: '1rem' }}>
          Agentic QA – Engineering Discipline Meets AI Productivity
        </h1>
        <h2 style={{ fontSize: '1.3rem', color: '#DB2C27', fontWeight: 600, marginBottom: '1.5rem' }}>
          “From vibe to validation: structured, agent-driven QA for modern sprints.”
        </h2>
      </section>

      {/* Agentic QA Lifecycle Section */}
      <section style={{ maxWidth: 1280, margin: '2rem auto 2.5rem auto', padding: '0 1.5rem' }}>
        <div style={{
          background: '#ffffff',
          borderRadius: 18,
          boxShadow: '0 12px 30px rgba(44,62,80,0.08)',
          padding: '2rem 1.5rem 1.5rem'
        }}>
          <h3 style={{
            color: '#2c3e50',
            fontSize: '1.65rem',
            fontWeight: 800,
            textAlign: 'center',
            marginBottom: '1.8rem'
          }}>
            Agentic QA Execution Flow
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            alignItems: 'stretch'
          }}>
            {qaLifecycleSteps.map((step, index) => (
              <div key={step.title} style={{ display: 'flex', flexDirection: 'column', minHeight: 340 }}>
                <div style={{
                  background: '#ff0f0f',
                  color: '#fff',
                  borderRadius: 20,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  fontSize: '1.04rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  padding: '1rem 0.75rem',
                  boxShadow: '0 8px 18px rgba(255,15,15,0.2)',
                  marginBottom: '0.9rem'
                }}>
                  {step.title}
                </div>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#3d3d3d',
                  lineHeight: 1.45,
                  fontSize: '0.98rem',
                  flexGrow: 1
                }}>
                  {step.points.map((point) => (
                    <li key={point} style={{ marginBottom: '0.45rem' }}>{point}</li>
                  ))}
                </ul>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1rem' }}>
                  <div style={{
                    width: 0,
                    height: 0,
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: '14px solid #ff0f0f',
                    marginBottom: '0.55rem'
                  }} />
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#ff0f0f', marginBottom: '0.5rem' }} />
                  <div style={{
                    width: 2,
                    height: 22,
                    borderLeft: '2px dashed #ff0f0f',
                    opacity: 0.7,
                    marginBottom: '0.45rem'
                  }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff0f0f', opacity: 0.9 }} />
                </div>

                {index < qaLifecycleSteps.length - 1 && (
                  <div style={{
                    height: 2,
                    borderTop: '2px dashed rgba(255,15,15,0.55)',
                    margin: '0.8rem 0 0'
                  }} />
                )}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '1.6rem',
            background: '#ff0f0f',
            borderRadius: 16,
            color: '#fff',
            padding: '1.35rem 1.2rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', textTransform: 'uppercase' }}>Human at the Center</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Higher Quality</div>
              <div style={{ fontSize: '1rem', lineHeight: 1.35 }}>Continuous human validation ensures reliable outcomes</div>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Faster Delivery</div>
              <div style={{ fontSize: '1rem', lineHeight: 1.35 }}>AI accelerates repetitive QA activities</div>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Better Coverage</div>
              <div style={{ fontSize: '1rem', lineHeight: 1.35 }}>Broader test possibilities and edge cases</div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={{ maxWidth: 900, margin: '2.5rem auto 2rem auto', background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.08)', padding: '2.5rem 2rem' }}>
        <h3 style={{ color: '#2c3e50', fontWeight: 700, fontSize: '1.4rem', marginBottom: '1rem' }}>What is Agentic QA?</h3>
        <p style={{ fontSize: '1.08rem', color: '#444', lineHeight: 1.7 }}>
          Agentic QA is about transforming random prompts into structured engineering. It ensures quality outputs by combining context creation, precise prompts, agent instructions, and workflow governance.
        </p>
      </section>

      {/* Core Components */}
      <section style={{ maxWidth: 900, margin: '0 auto 2.5rem auto', display: 'grid', gap: '2rem' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', boxShadow: '0 2px 12px rgba(44,62,80,0.08)' }}>
          <h4 style={{ color: '#DB2C27', fontWeight: 700, fontSize: '1.15rem' }}>Context Engineering</h4>
          <p style={{ color: '#333', margin: 0 }}>Define project-specific context to reduce noise and hallucination.</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', boxShadow: '0 2px 12px rgba(44,62,80,0.08)' }}>
          <h4 style={{ color: '#DB2C27', fontWeight: 700, fontSize: '1.15rem' }}>Prompt Discipline</h4>
          <p style={{ color: '#333', margin: 0 }}>Craft precise prompts that deliver 90% usable output in one go.</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', boxShadow: '0 2px 12px rgba(44,62,80,0.08)' }}>
          <h4 style={{ color: '#DB2C27', fontWeight: 700, fontSize: '1.15rem' }}>Agent Instructions</h4>
          <p style={{ color: '#333', margin: 0 }}>Provide clear, role-specific directives for autonomous QA agents.</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', boxShadow: '0 2px 12px rgba(44,62,80,0.08)' }}>
          <h4 style={{ color: '#DB2C27', fontWeight: 700, fontSize: '1.15rem' }}>Workflow Integration</h4>
          <p style={{ color: '#333', margin: 0 }}>Chain agents into SDLC workflows for seamless execution.</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', boxShadow: '0 2px 12px rgba(44,62,80,0.08)' }}>
          <h4 style={{ color: '#DB2C27', fontWeight: 700, fontSize: '1.15rem' }}>Governance & Validation</h4>
          <p style={{ color: '#333', margin: 0 }}>Ensure fairness, transparency, and disciplined validation of AI outputs.</p>
        </div>
      </section>

      {/* Practical Applications */}
      <section style={{ maxWidth: 900, margin: '0 auto 2.5rem auto', background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.08)', padding: '2.5rem 2rem' }}>
        <h3 style={{ color: '#2c3e50', fontWeight: 700, fontSize: '1.2rem', marginBottom: '1rem' }}>Practical Applications</h3>
        <ul style={{ color: '#444', fontSize: '1.05rem', lineHeight: 1.7, paddingLeft: '1.2em' }}>
          <li>API testing with Postman collections generated via AI.</li>
          <li>Regression testing focused only on modified features.</li>
          <li>Synthetic data generation for faster test environments.</li>
        </ul>
      </section>

      {/* Benefits */}
      <section style={{ maxWidth: 900, margin: '0 auto 2.5rem auto', background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.08)', padding: '2.5rem 2rem' }}>
        <h3 style={{ color: '#2c3e50', fontWeight: 700, fontSize: '1.2rem', marginBottom: '1rem' }}>Benefits</h3>
        <ul style={{ color: '#444', fontSize: '1.05rem', lineHeight: 1.7, paddingLeft: '1.2em' }}>
          <li>Faster QA cycles (3–5 day sprints).</li>
          <li>Reduced technical debt.</li>
          <li>Structured outputs (JSON, reusable templates).</li>
          <li>Empowering QA engineers to lead with AI productivity.</li>
        </ul>
      </section>

      {/* Call-to-Action */}
      <section style={{ textAlign: 'center', margin: '3rem 0 0 0' }}>
        <Link
          to="/workshops"
          className="button"
          style={{
            background: '#DB2C27',
            color: '#fff',
            padding: '16px 38px',
            borderRadius: '7px',
            fontWeight: 700,
            fontSize: '1.13rem',
            textDecoration: 'none',
            display: 'inline-block',
            boxShadow: '0 2px 12px rgba(218,41,28,0.10)',
            transition: 'background 0.2s ease',
            marginTop: '0.5rem'
          }}
        >
          Register for Agentic QA Workshop &rarr;
        </Link>
      </section>
    </div>
  );
}
